/**
 * Full-width slider of featured events. Sorted by date.
 * Card: image full slide, info strip overlaid at bottom (Instagram-style). Tap → event page.
 * Auto-advances when multiple slides; pauses on touch/mouse and resumes after 5s.
 * Dots indicate current slide when multiple events.
 */

import { useRef, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { useMediaQuery } from "../../lib/use-media-query.ts";
import {
  getCurrencySymbol,
  getMinPrice,
  formatEventDateShort,
  type EventWithPrices,
} from "../../lib/format.ts";
import type { Event } from "../../lib/graphql.ts";

const AUTO_ADVANCE_MS = 4500;
const RESUME_AFTER_MS = 5000;

function EventImage({ src }: { src: string }) {
  const [error, setError] = useState(false);
  if (error) {
    return (
      <div className="absolute inset-0 flex items-center justify-center bg-gradient-to-br from-amber-100/90 to-slate-100/90 text-slate-400 text-2xl">
        ★
      </div>
    );
  }
  return (
    <img
      src={src}
      alt=""
      loading="lazy"
      onError={() => setError(true)}
      className="absolute inset-0 w-full h-full object-cover"
    />
  );
}

interface EventCarouselProps {
  events: Event[];
}

export function EventCarousel({ events }: EventCarouselProps) {
  const isDesktop = useMediaQuery("(min-width: 1024px)");
  const containerRef = useRef<HTMLDivElement>(null);
  const intervalRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const resumeTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const [currentIndex, setCurrentIndex] = useState(0);

  useEffect(() => {
    if (events.length <= 1 || isDesktop) return;

    const el = containerRef.current;
    if (!el) return;

    const advance = () => {
      const width = el.offsetWidth;
      if (width <= 0) return;
      const idx = Math.round(el.scrollLeft / width);
      const nextIndex = (idx + 1) % events.length;
      setCurrentIndex(nextIndex);
      el.scrollTo({ left: nextIndex * width, behavior: "smooth" });
    };

    const startAuto = () => {
      if (intervalRef.current) return;
      intervalRef.current = setInterval(advance, AUTO_ADVANCE_MS);
    };

    const stopAuto = () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
        intervalRef.current = null;
      }
    };

    const scheduleResume = () => {
      if (resumeTimeoutRef.current) clearTimeout(resumeTimeoutRef.current);
      resumeTimeoutRef.current = setTimeout(() => {
        resumeTimeoutRef.current = null;
        startAuto();
      }, RESUME_AFTER_MS);
    };

    const onUserInteraction = () => {
      stopAuto();
      scheduleResume();
    };

    startAuto();
    el.addEventListener("touchstart", onUserInteraction, { passive: true });
    el.addEventListener("mousedown", onUserInteraction);
    el.addEventListener("wheel", onUserInteraction, { passive: true });

    return () => {
      stopAuto();
      if (resumeTimeoutRef.current) {
        clearTimeout(resumeTimeoutRef.current);
        resumeTimeoutRef.current = null;
      }
      el.removeEventListener("touchstart", onUserInteraction);
      el.removeEventListener("mousedown", onUserInteraction);
      el.removeEventListener("wheel", onUserInteraction);
    };
  }, [events.length, isDesktop]);

  useEffect(() => {
    const el = containerRef.current;
    if (!el || events.length <= 1) return;
    const onScroll = () => {
      const width = el.offsetWidth;
      if (width <= 0) return;
      setCurrentIndex(Math.round(el.scrollLeft / width));
    };
    el.addEventListener("scroll", onScroll);
    return () => el.removeEventListener("scroll", onScroll);
  }, [events.length]);

  if (events.length === 0) {
    return (
      <div className="rounded-2xl overflow-hidden bg-white/95 shadow-sm p-8 text-center">
        <p className="text-slate-500 text-sm font-medium uppercase tracking-wider">
          Скоро
        </p>
        <p className="text-slate-600 mt-1">Анонсы появятся здесь</p>
      </div>
    );
  }

  return (
    <div className="relative w-full">
      <div
        ref={containerRef}
        className={`flex overflow-x-auto overflow-y-visible snap-x snap-mandatory pb-6 scrollbar-hide lg:grid lg:grid-cols-1 lg:gap-y-6 lg:overflow-visible lg:snap-none lg:pb-0 lg:items-start`}
        style={
          isDesktop
            ? undefined
            : {
                scrollSnapType: "x mandatory",
                WebkitOverflowScrolling: "touch",
              }
        }
      >
        {events.map((event) => {
          const { price, currency } = getMinPrice(event as EventWithPrices);
          const symbol = getCurrencySymbol(currency);
          const priceLabel =
            price === null
              ? "Нет билетов"
              : price === 0
                ? "Бесплатно"
                : `от ${price} ${symbol}`;
          const dateStr = formatEventDateShort(event.startsAt);

          return (
            <Link
              key={event.id}
              to={`/event/${event.id}`}
              className="flex-shrink-0 w-full min-w-0 snap-start snap-always block no-underline text-inherit active:opacity-95"
              aria-label={`${event.title}, ${dateStr}`}
            >
              <div className="relative w-full overflow-visible">
                {/* Блок афиши: только картинка и градиент с текстом */}
                <div className="relative w-full h-[210px] xs:h-[240px] max-h-[280px] sm:max-h-[320px] lg:h-auto lg:aspect-[2/1] overflow-hidden rounded-2xl bg-slate-200 flex-shrink-0">
                  {(event.bannerImageUrl ?? event.imageUrl) ? (
                    <EventImage
                      src={event.bannerImageUrl ?? event.imageUrl ?? ""}
                    />
                  ) : (
                    <div className="absolute inset-0 flex items-center justify-center bg-gradient-to-br from-amber-100/90 to-slate-100/90 text-slate-400 text-2xl">
                      ★
                    </div>
                  )}
                  {/* Градиент и текст поверх афиши */}
                  <div
                    className="absolute inset-x-0 bottom-0 pt-8 pb-2.5 px-3 lg:pt-14 lg:pb-4 lg:px-5 bg-gradient-to-t from-black/85 via-black/40 to-transparent"
                    aria-hidden
                  >
                    <p className="text-white/90 text-[11px] xs:text-xs lg:text-base font-medium truncate">
                      {dateStr}
                    </p>
                    <p className="text-white font-semibold text-sm xs:text-base lg:text-xl leading-tight truncate mt-0.5 lg:mt-1">
                      {event.title}
                    </p>
                    <p className="text-amber-300 text-xs lg:text-base font-medium mt-0.5 lg:mt-1">
                      {priceLabel}
                    </p>
                  </div>
                </div>
                {/* Кнопка на границе: накладывается на афишу и на блок «С чего начать?» */}
                <span
                  className="absolute left-1/2 -translate-x-1/2 translate-y-1/2 bottom-0 z-[100] inline-flex items-center justify-center min-w-[60%] xs:min-w-[55%] max-w-[200px] sm:max-w-[240px] lg:max-w-[180px] px-6 py-2.5 xs:px-8 xs:py-3 lg:px-5 lg:py-2 text-xs xs:text-sm lg:text-sm font-medium text-amber-900 rounded-xl lg:rounded-lg bg-amber-400 hover:bg-amber-500 active:scale-[0.98] transition-colors shadow-md pointer-events-auto"
                  aria-hidden
                >
                  Принять участие
                </span>
              </div>
            </Link>
          );
        })}
      </div>
      {events.length > 1 && !isDesktop && (
        <div
          className="flex justify-center gap-1.5 mt-3"
          aria-hidden
        >
          {events.map((_, i) => (
            <span
              key={i}
              className={`block w-2 h-2 rounded-full transition-colors ${
                i === currentIndex ? "bg-amber-500" : "bg-slate-300"
              }`}
            />
          ))}
        </div>
      )}
    </div>
  );
}
